import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Video, Loader2, Circle, Square, Upload, AlertCircle } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../utils/firebase';
import { useAuth } from '../../utils/AuthContext';
import { usePortalHtmlBlobSrc } from '../../hooks/usePortalHtmlBlobSrc';
import { portalHostedAssetHref } from '../../utils/portalAssetUrl';
import { resolvePortalMarkupAuthorName } from '../../utils/portalMarkupAuthor';
import {
  createUploadingWalkthroughDoc,
  finalizePortalWalkthrough,
  newWalkthroughId,
  walkthroughStoragePath,
} from '../../utils/portalWalkthroughService';
import './PortalModelWalkthroughRecorder.css';

const MAX_RECORDING_SECONDS = 300;
const WARN_AT_SECONDS = 270;

export interface PortalModelWalkthroughRecorderProps {
  open: boolean;
  onClose: () => void;
  portalId: string;
  dealId: string;
  modelUrl: string;
  releaseId?: string;
  bundleId?: string;
  projectName?: string;
  clientDisplayName?: string;
  isStaffPortalUser?: boolean;
  onSubmitted?: () => void;
}

type RecorderPhase = 'preflight' | 'recording' | 'preview' | 'submitting' | 'done';

function pickRecorderMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return 'video/webm';
  if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) return 'video/webm;codecs=vp9';
  if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) return 'video/webm;codecs=vp8';
  if (MediaRecorder.isTypeSupported('video/webm')) return 'video/webm';
  return 'video/mp4';
}

const PortalModelWalkthroughRecorder: React.FC<PortalModelWalkthroughRecorderProps> = ({
  open,
  onClose,
  portalId,
  dealId,
  modelUrl,
  releaseId,
  bundleId,
  projectName,
  clientDisplayName,
  isStaffPortalUser = false,
  onSubmitted,
}) => {
  const { user } = useAuth();
  const fetchUrl = portalHostedAssetHref(modelUrl);
  const { iframeSrc, loadState, usedDirectFallback } = usePortalHtmlBlobSrc(modelUrl, fetchUrl);

  const [phase, setPhase] = useState<RecorderPhase>('preflight');
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);

  const webcamPreviewRef = useRef<HTMLVideoElement>(null);
  const displayVideoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const displayStreamRef = useRef<MediaStream | null>(null);
  const webcamStreamRef = useRef<MediaStream | null>(null);
  const compositeStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rafRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const durationRef = useRef(0);

  const authorName = resolvePortalMarkupAuthorName({
    isStaffPortalUser,
    userName: user?.name,
    userEmail: user?.email,
    clientDisplayName: clientDisplayName ?? projectName,
  });

  const cleanupStreams = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    mediaRecorderRef.current = null;
    [displayStreamRef, webcamStreamRef, compositeStreamRef].forEach(streamRef => {
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    });
    if (webcamPreviewRef.current) webcamPreviewRef.current.srcObject = null;
    if (displayVideoRef.current) displayVideoRef.current.srcObject = null;
  }, []);

  const reset = useCallback(() => {
    cleanupStreams();
    setPhase('preflight');
    setError(null);
    setElapsed(0);
    durationRef.current = 0;
    setPreviewUrl(null);
    setRecordingBlob(null);
    chunksRef.current = [];
  }, [cleanupStreams]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  useEffect(() => () => cleanupStreams(), [cleanupStreams]);

  const startCompositor = useCallback(
    (displayStream: MediaStream, webcamStream: MediaStream): MediaStream => {
      const displayTrack = displayStream.getVideoTracks()[0];
      const settings = displayTrack.getSettings();
      const width = settings.width || 1280;
      const height = settings.height || 720;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvasRef.current = canvas;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');

      const displayVideo = displayVideoRef.current || document.createElement('video');
      displayVideo.muted = true;
      displayVideo.playsInline = true;
      displayVideo.srcObject = displayStream;
      void displayVideo.play();

      const webcamVideo = webcamPreviewRef.current;
      if (webcamVideo) {
        webcamVideo.srcObject = webcamStream;
        void webcamVideo.play();
      }

      const draw = () => {
        if (!ctx || !canvasRef.current) return;
        try {
          ctx.drawImage(displayVideo, 0, 0, width, height);
          const pipSource = webcamPreviewRef.current;
          if (pipSource && pipSource.readyState >= 2) {
            const pipW = Math.round(width * 0.22);
            const pipH = Math.round(pipW * 0.75);
            const margin = Math.round(width * 0.02);
            const x = width - pipW - margin;
            const y = height - pipH - margin;
            ctx.save();
            ctx.beginPath();
            const r = 12;
            ctx.moveTo(x + r, y);
            ctx.arcTo(x + pipW, y, x + pipW, y + pipH, r);
            ctx.arcTo(x + pipW, y + pipH, x, y + pipH, r);
            ctx.arcTo(x, y + pipH, x, y, r);
            ctx.arcTo(x, y, x + pipW, y, r);
            ctx.closePath();
            ctx.clip();
            ctx.drawImage(pipSource, x, y, pipW, pipH);
            ctx.restore();
          }
        } catch {
          /* frame skip */
        }
        rafRef.current = requestAnimationFrame(draw);
      };
      draw();

      const out = canvas.captureStream(30);
      const mic = webcamStream.getAudioTracks()[0];
      if (mic) out.addTrack(mic);
      compositeStreamRef.current = out;
      return out;
    },
    [],
  );

  const startRecording = async () => {
    setError(null);
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
        } as MediaTrackConstraints,
        audio: false,
      });
      displayStreamRef.current = displayStream;

      const webcamStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      webcamStreamRef.current = webcamStream;

      displayStream.getVideoTracks()[0]?.addEventListener('ended', () => {
        stopRecording();
      });

      const composite = startCompositor(displayStream, webcamStream);
      const mimeType = pickRecorderMimeType();
      const recorder = new MediaRecorder(composite, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordingBlob(blob);
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        cleanupStreams();
        setPhase('preview');
      };

      mediaRecorderRef.current = recorder;
      recorder.start(1000);
      setPhase('recording');
      setElapsed(0);
      durationRef.current = 0;

      timerRef.current = setInterval(() => {
        setElapsed(prev => {
          const next = prev + 1;
          durationRef.current = next;
          if (next >= MAX_RECORDING_SECONDS) {
            stopRecording();
            return MAX_RECORDING_SECONDS;
          }
          return next;
        });
      }, 1000);
    } catch (err: unknown) {
      cleanupStreams();
      const msg = err instanceof Error ? err.message : 'Could not start recording';
      setError(
        msg.includes('NotAllowed') || msg.includes('Permission')
          ? 'Screen or camera permission was denied. Allow access and try again.'
          : msg,
      );
      setPhase('preflight');
    }
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    } else {
      cleanupStreams();
    }
  };

  const submitWalkthrough = async () => {
    if (!recordingBlob || !user?.id) return;
    setPhase('submitting');
    setError(null);
    const walkthroughId = newWalkthroughId();
    const ext = recordingBlob.type.includes('webm') ? 'webm' : 'mp4';
    const storagePath = walkthroughStoragePath(portalId, dealId, walkthroughId, ext);

    try {
      await createUploadingWalkthroughDoc(portalId, walkthroughId, {
        portalId,
        dealId,
        releaseId,
        bundleId,
        storagePath,
        downloadUrl: '',
        durationSeconds: durationRef.current,
        authorUid: user.id,
        authorName,
      });

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, recordingBlob, { contentType: recordingBlob.type });
      const downloadUrl = await getDownloadURL(storageRef);

      await finalizePortalWalkthrough({
        portalId,
        dealId,
        walkthroughId,
        storagePath,
        downloadUrl,
        durationSeconds: durationRef.current,
        releaseId,
        bundleId,
        authorName,
      });

      onSubmitted?.();
      setPhase('done');
    } catch (err) {
      console.error('[PortalModelWalkthroughRecorder] submit failed', err);
      setError('Upload failed. Please try again.');
      setPhase('preview');
    }
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (!open) return null;

  return (
    <div className="pmwr-overlay" role="presentation">
      <div className="pmwr-shell" role="dialog" aria-modal="true" aria-label="Record 3D walkthrough">
        <header className="pmwr-header">
          <h2>
            <Video size={18} aria-hidden />
            Record walkthrough
            <span className="pmwr-beta">Beta</span>
          </h2>
          <button type="button" className="pmwr-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pmwr-body">
          {phase === 'preflight' && (
            <div className="pmwr-preflight">
              <p className="pmwr-copy">
                Walk us through your 3D model on video — up to 5 minutes. We&apos;ll share your recording
                with your project team and summarize your questions and concerns.
              </p>
              <ol className="pmwr-steps">
                <li>Click <strong>Start recording</strong> and choose <strong>This tab</strong> when prompted.</li>
                <li>Explore the model while narrating your feedback.</li>
                <li>Stop when finished, preview, and submit to your team.</li>
              </ol>
              {usedDirectFallback && loadState === 'ready' && (
                <p className="pmwr-copy" style={{ color: '#b45309' }}>
                  <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Large model loaded in fallback mode — if recording looks wrong, open the model in a new tab
                  and share that tab instead.
                </p>
              )}
              {error && <p className="pmwr-error" role="alert">{error}</p>}
              <div>
                <button type="button" className="pmwr-btn pmwr-btn-primary" onClick={() => void startRecording()}>
                  <Circle size={14} fill="currentColor" />
                  Start recording
                </button>
              </div>
            </div>
          )}

          {(phase === 'recording' || phase === 'submitting') && (
            <div className="pmwr-recording-layout">
              <div className="pmwr-stage">
                {loadState === 'loading' && (
                  <div className="pmwr-model-loading">
                    <Loader2 size={22} className="cps-spinner" />
                    Loading 3D model…
                  </div>
                )}
                {loadState === 'error' && (
                  <div className="pmwr-model-error">
                    Could not embed the model. Open it in a new tab and share that tab when recording.
                  </div>
                )}
                {iframeSrc && loadState === 'ready' && (
                  <iframe
                    src={iframeSrc}
                    title="3D model"
                    className="pmwr-model-iframe"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-pointer-lock allow-fullscreen"
                    allow="fullscreen"
                  />
                )}
                <video ref={displayVideoRef} style={{ display: 'none' }} muted playsInline />
              </div>
              <aside className="pmwr-sidebar">
                <div className={`pmwr-timer ${elapsed >= WARN_AT_SECONDS ? 'warn' : ''}`}>
                  {formatTime(elapsed)} / {formatTime(MAX_RECORDING_SECONDS)}
                </div>
                <video ref={webcamPreviewRef} className="pmwr-webcam-preview" muted playsInline />
                <div className="pmwr-controls">
                  {phase === 'recording' ? (
                    <button type="button" className="pmwr-btn pmwr-btn-primary" onClick={stopRecording}>
                      <Square size={14} fill="currentColor" />
                      Stop recording
                    </button>
                  ) : (
                    <button type="button" className="pmwr-btn pmwr-btn-primary" disabled>
                      <Loader2 size={14} className="cps-spinner" />
                      Uploading…
                    </button>
                  )}
                </div>
                {elapsed >= WARN_AT_SECONDS && phase === 'recording' && (
                  <p className="pmwr-copy" style={{ color: '#b45309', fontSize: 13 }}>
                    Recording will stop automatically at 5:00.
                  </p>
                )}
              </aside>
            </div>
          )}

          {phase === 'preview' && previewUrl && (
            <div className="pmwr-preview">
              <p className="pmwr-copy">Preview your walkthrough before sending it to your project team.</p>
              <video src={previewUrl} controls playsInline />
              {error && <p className="pmwr-error" role="alert">{error}</p>}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="pmwr-btn pmwr-btn-primary" onClick={() => void submitWalkthrough()}>
                  <Upload size={14} />
                  Submit to your project team
                </button>
                <button type="button" className="pmwr-btn pmwr-btn-secondary" onClick={reset}>
                  Re-record
                </button>
              </div>
            </div>
          )}

          {phase === 'done' && (
            <div className="pmwr-preview">
              <p className="pmwr-copy">
                <strong>Thank you!</strong> Your walkthrough was submitted. Your project team will review it
                and follow up with any questions.
              </p>
              <button type="button" className="pmwr-btn pmwr-btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalModelWalkthroughRecorder;
